// src/app/shared/services/auth.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  SilentRequest
} from '@azure/msal-browser';
import { Observable, of, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private readonly LOGIN_SCOPES = ['openid', 'profile'];
  private readonly API_SCOPES = [environment.apiScope];

  private refreshTimerId: any = null;
  private eventsSub: Subscription | null = null;
  private initInProgress = false;
  private initCompleted = false;
  
  // Current encrypted storage keys (v2)
  private readonly STORAGE_KEY = 'app_encrypted_access_token_v2';         // AES-GCM encrypted access token
  private readonly STORAGE_EXP_KEY = 'app_encrypted_access_token_exp_v2'; // AES-GCM encrypted expiry time
  private readonly SALT_KEY = 'app_token_salt_v2';                        // PBKDF2 salt (persists across sessions, not secret)
  private readonly KDF_ITERATIONS = 200_000;

  // Old keys from previous implementation (for cleanup)
  private readonly OLD_STORAGE_KEY = 'app_encoded_access_token';
  private readonly OLD_STORAGE_EXP_KEY = 'app_encoded_access_token_exp';

  constructor(private msal: MsalService, private broadcast: MsalBroadcastService) {
    // Clean up old base64-encoded keys from previous implementation
    this.cleanupOldKeys();
    
    // optional: listen to msal events if you need to react
    this.eventsSub = this.broadcast.msalSubject$.subscribe(() => {
      // no-op placeholder
    });
  }

  ngOnDestroy(): void {
    this.eventsSub?.unsubscribe();
    if (this.refreshTimerId) clearTimeout(this.refreshTimerId);
  }

  // Called at app startup to handle redirect responses and ensure user is authenticated.
  async initOnStart(): Promise<string> {
    // Prevent multiple simultaneous calls
    if (this.initInProgress) {
      return '';
    }
    
    if (this.initCompleted) {
      return '';
    }
    
    this.initInProgress = true;
    
    try {
      // Handle redirect response (this will be null if not redirecting back from login)
      const result = await this.msal.instance.handleRedirectPromise();
      
      if (result) {
        // Successfully returned from redirect with token
        if (result.accessToken) {
          await this.persistTokenAndScheduleRefresh(result);
          return String(result.accessToken);
        }
      }

      // Check if we already have accounts (user previously logged in)
      const accounts = this.msal.instance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        // No accounts - need to login
        // Only redirect if we're not already in the middle of a redirect
        if (!window.location.hash.includes('code=') && !window.location.hash.includes('error=')) {
          await this.loginRedirect();
        }
        return '';
      } else {
        // Have account - try to get token silently
        const token = await this.getAccessToken();
        return token;
      }
    } catch (err) {

      // Only redirect if we're not already in a redirect callback
      if (!window.location.hash.includes('code=') && !window.location.hash.includes('error=')) {
        await this.loginRedirect();
      }
      return '';
    } finally {
      this.initInProgress = false;
      this.initCompleted = true;

    }
  }

    /**
   * Returns the display name or username of the currently logged-in user.
   * Falls back to email or empty string if not available.
   */
  getUserName(): string {
    const account = this.getActiveAccount();
    if (!account) return '';
    // Try to return the display name, then username, then email
    return account.name || account.username || '';
  }

  /**
   * Returns the email of the currently logged-in user.
   * Returns Observable with email string or empty string if not available.
   */
  getUserEmail(): string {
    const account = this.getActiveAccount();
    if (!account) {
      return '';
    }
    // Try to return the email or username
    return (account.username || '');
  }

  /**
   * Returns the roles array from the user's id_token claims.
   * Extracts the "roles" claim from the identity token.
   * Returns an empty array if no roles claim is present or user is not authenticated.
   */
  getUserRoles(): string[] {
    const account = this.getActiveAccount();
    if (!account) {
      return [];
    }

    // Access idTokenClaims from the account
    const idTokenClaims = account.idTokenClaims as any;
    if (!idTokenClaims) {
      return [];
    }

    // Extract roles array from claims (typically "roles" claim)
    const roles = idTokenClaims.roles;
    if (!Array.isArray(roles)) {
      return [];
    }

    return roles;
  }

  /**
   * Checks if the current user has the 'isUKIadmin' role.
   * Returns true if the role is present, false otherwise.
   */
  hasIsUKIAdminRole(): boolean {
    const roles = this.getUserRoles();
    const hasRole = roles.includes('isUKIadmin');
    return hasRole;
  }

  hasAdminRole(): boolean {
    const roles = this.getUserRoles();
    const hasRole = roles.includes('isAdmin');
    return hasRole;
  }

  hasUserReadOnlyRole(): boolean {
    const roles = this.getUserRoles();
    return roles.includes('UserReadOnly');
  }

  async loginRedirect(scopes: string[] = [...this.LOGIN_SCOPES, ...this.API_SCOPES]): Promise<void> {
    try {
      await this.msal.instance.loginRedirect({
        scopes,
        redirectUri: (environment.msal && environment.msal.redirectUri) || window.location.origin
      });
    } catch {
      // loginRedirect navigates away; swallow errors
    }
  }

  async logout(): Promise<void> {
    try {
      const account = this.getActiveAccount();
      this.clearTokenStorage();
      
      if (account) {
        await this.msal.instance.logoutRedirect({
          account,
          postLogoutRedirectUri: (environment.msal && environment.msal.redirectUri) || window.location.origin
        });
      } else {
        await this.msal.instance.logoutRedirect({
          postLogoutRedirectUri: (environment.msal && environment.msal.redirectUri) || window.location.origin
        });
      }
    } catch {
      // ignore
    }
  }

  // Return access token string or '' if not available. Always async.
  async getAccessToken(scopes: string[] = this.API_SCOPES): Promise<string> {
    try {
      const account = this.getActiveAccount();
      if (!account) {
        await this.loginRedirect(scopes);
        return '';
      }

      const silentRequest: SilentRequest = {
        account,
        scopes
      };

      const result = await this.msal.instance.acquireTokenSilent(silentRequest);
      if (result && result.accessToken) {
        await this.persistTokenAndScheduleRefresh(result);
        return String(result.accessToken);
      }
    } catch (err) {
      // silent failed -> fallback to redirect
      await this.loginRedirect(scopes);
      return '';
    }
    return '';
  }

  // Returns decrypted token or '' if none
  async getDecodedCachedToken(): Promise<string> {
    const encrypted = localStorage.getItem(this.STORAGE_KEY);
    if (!encrypted) return '';
    try {
      const keyMaterial = await this.getKeyMaterial(environment.tokenEncryptionKey);
      const salt = await this.getOrCreateSalt();
      const key = await this.deriveKey(keyMaterial, salt, this.KDF_ITERATIONS);
      const decrypted = await this.decryptStringWithKey(encrypted, key);
      return String(decrypted);
    } catch (e) {
      console.warn('getDecodedCachedToken: decrypt failed', e);
      return '';
    }
  }

  // --- Internal helpers ---

  private getActiveAccount(): AccountInfo | null {
    const accounts = this.msal.instance.getAllAccounts();
    return accounts && accounts.length > 0 ? accounts[0] : null;
  }

  // Clear only token and expiry keys, preserve salt for future use
  private clearTokenStorage(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STORAGE_EXP_KEY);
  }

  // Remove old keys from previous base64 implementation
  private cleanupOldKeys(): void {
    localStorage.removeItem(this.OLD_STORAGE_KEY);
    localStorage.removeItem(this.OLD_STORAGE_EXP_KEY);
  }

  private async persistTokenAndScheduleRefresh(result: AuthenticationResult): Promise<void> {
    const token = result.accessToken ?? '';
    const expiresOn = result.expiresOn ?? new Date(Date.now() + 60 * 60 * 1000);

    try {
      const keyMaterial = await this.getKeyMaterial(environment.tokenEncryptionKey);
      const salt = await this.getOrCreateSalt();
      const key = await this.deriveKey(keyMaterial, salt, this.KDF_ITERATIONS);

      const encryptedToken = await this.encryptStringWithKey(String(token), key);
      const encryptedExp = await this.encryptStringWithKey(expiresOn.toISOString(), key);

      localStorage.setItem(this.STORAGE_KEY, encryptedToken);
      localStorage.setItem(this.STORAGE_EXP_KEY, encryptedExp);
    } catch (e) {
      console.warn('persistToken: encryption/storage failed', e);
    }

    // schedule refresh 60s before expiry
    const expiresAt = (result.expiresOn ?? new Date(Date.now() + 60 * 60 * 1000)).getTime();
    const refreshAt = Math.max(expiresAt - 60 * 1000, Date.now() + 5 * 1000);
    const delay = Math.max(refreshAt - Date.now(), 0);

    if (this.refreshTimerId) clearTimeout(this.refreshTimerId);
    this.refreshTimerId = setTimeout(async () => {
      await this.performSilentRefresh();
    }, delay);
  }

  private async performSilentRefresh(): Promise<void> {
    const account = this.getActiveAccount();
    if (!account) {
      await this.loginRedirect();
      return;
    }
    try {
      const silentRequest: SilentRequest = { account, scopes: this.API_SCOPES };
      const result = await this.msal.instance.acquireTokenSilent(silentRequest);
      if (result && result.accessToken) {
        await this.persistTokenAndScheduleRefresh(result);
      } else {
        await this.loginRedirect();
      }
    } catch {
      await this.loginRedirect();
    }
  }

  // ===== Web Crypto helpers (AES-GCM with PBKDF2-derived key) =====
  // These helpers intentionally return/consume strings so callers remain simple.

  private async getOrCreateSalt(): Promise<Uint8Array> {
    const existing = localStorage.getItem(this.SALT_KEY);
    if (existing) {
      return this.base64ToBytes(existing);
    }
    const salt = crypto.getRandomValues(new Uint8Array(16));
    localStorage.setItem(this.SALT_KEY, this.bytesToBase64(salt));
    return salt;
  }

  private async getKeyMaterial(passphrase: string): Promise<CryptoKey> {
    const enc = new TextEncoder();
    return crypto.subtle.importKey('raw', enc.encode(passphrase), { name: 'PBKDF2' }, false, ['deriveKey']);
  }

  private async deriveKey(keyMaterial: CryptoKey, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt as BufferSource,
        iterations,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  // layout: iv(12) | ciphertext
  private async encryptStringWithKey(plain: string, key: CryptoKey): Promise<string> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const enc = new TextEncoder();
    const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plain));
    const combined = new Uint8Array(iv.length + cipher.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(cipher), iv.length);
    return this.bytesToBase64(combined);
  }

  private async decryptStringWithKey(encryptedB64: string, key: CryptoKey): Promise<string> {
    const combined = this.base64ToBytesSync(encryptedB64);
    const iv = combined.subarray(0, 12);
    const cipherBytes = combined.subarray(12);
    const plainBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      key,
      cipherBytes as BufferSource
    );
    return new TextDecoder().decode(plainBuffer);
  }

  // Utility: base64 <-> bytes
  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
    }
    return btoa(binary);
  }

  private async base64ToBytes(b64: string): Promise<Uint8Array> {
    const bin = atob(b64);
    const len = bin.length;
    const buffer = new ArrayBuffer(len);
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }

  // synchronous variant used only after reading string into memory
  private base64ToBytesSync(b64: string): Uint8Array {
    const bin = atob(b64);
    const len = bin.length;
    const buffer = new ArrayBuffer(len);
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }
}